import {
  EditOutlined,
  CheckOutlined,
  SearchOutlined,
  SettingOutlined,
  InboxOutlined,
  UserOutlined,
  DiffOutlined,
} from "@ant-design/icons";
import { Checkbox, Button, Tooltip, Select, Input } from "antd";
import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommCheckModal from "./CommCheckModal";
import { roleBasedAccessControlConstants } from "../../constants";
import EditDeviceModal from "../../helpers/admin/EditDeviceModal";
import {
  transformDeviceData,
  statusDisplayMap,
  statusOptions,
  getAvailableStatusOptions,
  isStatusFieldDisabled,
} from "../../helpers/admin/statusUtils";
import { hasEditPermission } from "../../helpers/rabc-helper";
import { GET_PROJECT_REQUEST } from "../../redux/data360Live_Project/types";
import {
  fetchDevices,
  commCheckRequest,
} from "../../redux/deviceRegister/action";
import CommonLoader from "../Loaders/loader";

import "../../global-styles/deviceManagement.scss";

const DeviceCardView = () => {
  const dispatch = useDispatch();

  const { devices, isLoading } = useSelector((state) => state.deviceIdReducer);
  const userDetails = useSelector((state) => state.auth?.userDetails);

  const projectList = useSelector(
    (state) => state.data360LiveProjectReducer?.projectDetails?.projects || []
  );

  const [selectedDeviceKeys, setSelectedDeviceKeys] = useState([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [bulkProjectIndex, setBulkProjectIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");

  // Get user permissions
  const canEdit = hasEditPermission(
    userDetails,
    roleBasedAccessControlConstants.scopes.admin.edit
  );

  useEffect(() => {
    dispatch(fetchDevices());
    dispatch({ type: GET_PROJECT_REQUEST });
  }, [dispatch]);

  const projectNames = useMemo(
    () => projectList?.map((item) => item.project_name) || [],
    [projectList]
  );
  const projectNumber = useMemo(
    () => projectList?.map((item) => item.project_number) || [],
    [projectList]
  );
  const projectId = useMemo(
    () => projectList?.map((item) => item.project_id) || [],
    [projectList]
  );

  const rowData = useMemo(() => transformDeviceData(devices), [devices]);
  const filteredDevices = useMemo(() => {
    return rowData.filter((device) => {
      const matchesSearch =
        searchTerm.trim() === "" ||
        device.DeviceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (device._original?.deviceName || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || device.Status === statusFilter;
      const matchesProject =
        projectFilter === "all" ||
        (device.Project && device.Project.includes(projectFilter));

      return matchesSearch && matchesStatus && matchesProject;
    });
  }, [rowData, searchTerm, statusFilter, projectFilter]);

  const handleDeviceSelect = (deviceId) => {
    setSelectedDeviceKeys((prev) => {
      const newSelection = prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId];

      if (newSelection.length === 0) {
        setBulkProjectIndex(null);
      }

      return newSelection;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedDeviceKeys(filteredDevices.map((device) => device.DeviceId));
    } else {
      setSelectedDeviceKeys([]);
      setBulkProjectIndex(null);
    }
  };

  const handleInlineBulkAssign = async () => {
    if (bulkProjectIndex === null) {
      if (window && window.antd)
        window.antd.message.warning("Please select a project");
      return;
    }
    setBulkAssignLoading(true);
    try {
      const projectName = projectNames[bulkProjectIndex];
      const projectNum = projectNumber[bulkProjectIndex];
      const project_Id = projectId[bulkProjectIndex];
      await dispatch({
        type: "@@deviceId/ASSIGN_PROJECT_TO_DEVICEID",
        payload: {
          deviceIds: selectedDeviceKeys,
          projectName: projectName,
          projectNumber: projectNum,
          projectId: project_Id,
        },
      });
      setSelectedDeviceKeys([]);
      setBulkProjectIndex(null);
    } catch (error) {
      if (window && window.antd)
        window.antd.notification.error({
          message: "Failed to assign devices",
          description: error.message,
        });
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const handleEdit = useCallback((device) => {
    setEditingDevice(device);
    setIsEditModalVisible(true);
  }, []);

  const showModal = (device) => {
    dispatch(commCheckRequest({ Edge_UID: device.DeviceId }));
    setIsModalVisible(true);
  };

  if (isLoading && !devices.length) {
    return (
      <div className="slogdata-loader">
        <CommonLoader />
      </div>
    );
  }

  return (
    <div className="device-management-container">
      <div className="stats-grid">
        {Object.entries({
          "Active & Registered": ["DownloadComplete"],
          "Device Ready": ["PublishReady"],
          "Pending Registration": [
            "Pending",
            "CertificateAvailable",
            "Register",
            "AdminApproved",
          ],
          "Waiting For Approval": ["AdminApproval"],
          "Registration Failed": ["Failed"],
          "Decommissioned Devices": ["Deregistered", "AdminRejected"],
        }).map(([category, statuses]) => {
          const count = filteredDevices.filter((d) =>
            statuses.includes(d.Status)
          ).length;
          return (
            <div key={category} className="stat-card">
              <div className="stat-header">
                <span className="stat-title">{category}</span>
                <span
                  className={`stat-indicator ${category
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")}`}
                ></span>
              </div>
              <div className="stat-value">{count}</div>
            </div>
          );
        })}
      </div>

      <div className="filters-section">
        <div className="filters-row">
          <div className="search-input">
            <SearchOutlined className="search-icon" />
            <Input
              type="text"
              placeholder="Search devices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select
            className="filter-select"
            value={projectFilter}
            onChange={setProjectFilter}
            placeholder="Filter by Project"
            style={{ minWidth: 200 }}
          >
            <Select.Option value="all">All Projects</Select.Option>
            {projectNames.map((project, idx) => (
              <Select.Option key={idx} value={project}>
                {project}(#{projectNumber[idx]})
              </Select.Option>
            ))}
          </Select>
          <Select
            className="filter-select"
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <Select.Option value="all">All Statuses</Select.Option>
            {statusOptions.map((status) => (
              <Select.Option key={status} value={status}>
                {statusDisplayMap[status] || status}
              </Select.Option>
            ))}
          </Select>
        </div>
        {selectedDeviceKeys.length > 0 && canEdit && (
          <div className="bulk-actions">
            <div className="selection-info">
              <span>
                {selectedDeviceKeys.length} device
                {selectedDeviceKeys.length !== 1 ? "s" : ""} selected
              </span>
            </div>
            <div className="action-buttons">
              <div className="action-group">
                <Select
                  key={`project-select-${selectedDeviceKeys.length}`}
                  className="action-select"
                  value={bulkProjectIndex}
                  onChange={setBulkProjectIndex}
                  placeholder="Assign to Project..."
                  showSearch
                  optionFilterProp="children"
                  style={{ minWidth: 200 }}
                  disabled={selectedDeviceKeys.length === 0}
                >
                  {projectNames.map((project, idx) => (
                    <Select.Option key={idx} value={idx}>
                      {project}(#{projectNumber[idx]})
                    </Select.Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  onClick={handleInlineBulkAssign}
                  disabled={bulkProjectIndex === null || bulkAssignLoading}
                  icon={<CheckOutlined className="checkicon" />}
                  loading={bulkAssignLoading}
                >
                  Assign Project
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {canEdit && (
        <div className="select-all">
          <Checkbox
            checked={
              selectedDeviceKeys.length === filteredDevices.length &&
              filteredDevices.length > 0
            }
            onChange={handleSelectAll}
          />
          <span>Select All</span>
        </div>
      )}

      <div className="devices-grid">
        {filteredDevices.map((device) => (
          <div key={device.DeviceId} className="device-card">
            {canEdit && (
              <div className="device-checkbox">
                <Checkbox
                  checked={selectedDeviceKeys.includes(device.DeviceId)}
                  onChange={() => handleDeviceSelect(device.DeviceId)}
                />
              </div>
            )}
            <div className="device-content">
              <div className="device-header">
                <Tooltip
                  title={`Device ID: ${device.DeviceId}`}
                  placement="top"
                >
                  <h3 className="device-id">{device.DeviceId}</h3>
                </Tooltip>
                <span className={`status-badge ${device.Status.toLowerCase()}`}>
                  {device.StatusDisplay}
                </span>
              </div>
              <div className="device-details">
                <div className="detail-row">
                  <UserOutlined />
                  <Tooltip title={device.Project}>
                    <span className="project-tooltip">
                      Project: {device.Project}
                    </span>
                  </Tooltip>
                </div>
                <div className="detail-row">
                  <InboxOutlined />
                  <span> weld Type: {device.weldType}</span>
                </div>
                <div className="detail-row">
                  <DiffOutlined />
                  <span>welduid: {device.welduid}</span>
                </div>
              </div>
            </div>
            {canEdit && (
              <div className="device-footer">
                <div className="firmware-info">
                  {/* Updated:{" "}
                {device.updatedAt
                  ? new Date(device.updatedAt).toLocaleDateString("en-GB")
                  : "-"} */}
                </div>
                <div className="footer-icons">
                  <Tooltip title="Edit Device">
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(device)}
                    />
                  </Tooltip>
                  <Button
                    type="text"
                    icon={<SettingOutlined className="setting-icon" />}
                    onClick={() => showModal(device)}
                    disabled={!(device.Status === "PublishReady")}
                    className={`${
                      !(device.Status === "PublishReady")
                        ? "disabled-button"
                        : ""
                    }`}
                  >
                    Manage
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <EditDeviceModal
        visible={isEditModalVisible}
        device={editingDevice}
        projectNames={projectNames}
        projectNumber={projectNumber}
        projectId={projectId}
        statusDisplayMap={statusDisplayMap}
        getAvailableStatusOptions={getAvailableStatusOptions}
        isStatusFieldDisabled={isStatusFieldDisabled}
        onCancel={() => setIsEditModalVisible(false)}
      />
      <CommCheckModal
        isModalVisible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
      />
    </div>
  );
};

export default DeviceCardView;
